import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Camera } from 'lucide-react';
import { useAuthStore } from '../stores/authStore';

const AuthSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { checkAuth } = useAuthStore();
  const [connectedProvider, setConnectedProvider] = useState('');

  // Helper function to get provider display name
  const getProviderDisplayName = (provider) => {
    const names = {
      'flickr': 'Flickr',
      'unsplash': 'Unsplash',
      'smugmug': 'SmugMug'
    };
    return names[provider] || provider;
  };

  // Helper function to determine which provider was most recently connected
  const getMostRecentProvider = (user) => {
    if (!user?.connectedAccounts || user.connectedAccounts.length === 0) {
      return null;
    }

    // Sort accounts by lastUsed or connectedAt timestamp (most recent first)
    const sortedAccounts = [...user.connectedAccounts].sort((a, b) => {
      const dateA = new Date(a.lastUsed || a.connectedAt);
      const dateB = new Date(b.lastUsed || b.connectedAt);
      return dateB.getTime() - dateA.getTime();
    });

    return sortedAccounts[0]?.provider;
  };

  useEffect(() => {
    const initializeUser = async () => {
      try {
        console.log('AuthSuccess: Checking authentication...');
        const user = await checkAuth();
        console.log('AuthSuccess: Auth check result:', user);
        
        if (user) {
          // Determine which provider was connected
          const provider = searchParams.get('provider') || getMostRecentProvider(user);
          setConnectedProvider(provider);
          
          console.log('AuthSuccess: User authenticated, connected provider:', provider);
          // Redirect to dashboard after a short delay
          setTimeout(() => {
            navigate('/', { replace: true });
          }, 2000);
        } else {
          console.log('AuthSuccess: No user found, redirecting to login...');
          navigate('/login', { replace: true });
        }
      } catch (error) {
        console.error('AuthSuccess: Failed to initialize user:', error);
        navigate('/login', { replace: true });
      }
    };

    initializeUser();
  }, [checkAuth, navigate, searchParams]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-green-100">
            <CheckCircle className="h-6 w-6 text-green-600" />
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Successfully Connected!
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Your {connectedProvider ? getProviderDisplayName(connectedProvider) : 'photo platform'} account has been connected to {process.env.REACT_APP_NAME || 'Lenscribe'}
          </p>
        </div>
        
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <div className="text-center">
            <Camera className="mx-auto h-12 w-12 text-primary-600 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Welcome to {process.env.REACT_APP_NAME || 'Lenscribe'}
            </h3>
            <p className="text-sm text-gray-600 mb-6">
              You're all set! You can now start enhancing your {connectedProvider ? getProviderDisplayName(connectedProvider) : ''} photos with AI-generated metadata.
            </p>
            
            <div className="animate-pulse">
              <div className="text-sm text-gray-500">
                Redirecting to dashboard...
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthSuccess;
